import { index, modelOptions, mongoose, post, prop } from "@typegoose/typegoose";
import { getZodSchemaFromClass, ObjectIdString, validate } from "../../zod-decorator/index.js";
import { ScdDocument } from "../scdDocument/scdDocument.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../schemaBase.js";
import type {
    NotificationSubscriptionMembershipFieldsWithDefaults,
    NotificationSubscriptionMembershipSchemaTypes,
} from "../types/notificationSubscriptionMembership.d.js";
import { type DtoValidatorTypes } from "../validatableClass.js";

const SCHEMA_KEY = "notificationSubscriptionMemberships" as const;

// Links a phone number to a subscription. SCD Type-2: the current non-deleted version is an active
// member; leaving is a logical delete that keeps the join/leave history.
@index(
    { subscription: 1, addressRecordId: 1 },
    { unique: true, partialFilterExpression: { versionIsCurrent: true, deleted: false } },
)
// Looked up both ways: by subscription (warning → members) and by address (compose → subscriptions).
@index({ subscription: 1 })
@index({ addressRecordId: 1 })
@modelOptions({
    schemaOptions: {
        ...getDefaultSchemaOptions(SCHEMA_KEY),
        collection: "notificationSubscriptionMemberships" as const satisfies typeof SCHEMA_KEY,
        _id: true,
    },
})
@post<SchemaBase<NotificationSubscriptionMembership>>(...addSchemaKeyHook(SCHEMA_KEY))
export class NotificationSubscriptionMembership extends ScdDocument<
    NotificationSubscriptionMembership,
    NotificationSubscriptionMembershipFieldsWithDefaults
> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    @validate({ zodValidator: ObjectIdString, responseOnly: true })
    public _id!: mongoose.Types.ObjectId;

    /**
     * The subscription this membership grants reach through — its stable SCD recordId, so the
     * membership survives subscription edits. No ref: recordIds are not version `_id`s, so this
     * field cannot be populated.
     */
    @validate({ zodValidator: ObjectIdString })
    @prop({ type: () => mongoose.Types.ObjectId, required: true })
    public subscription!: mongoose.Types.ObjectId;

    /**
     * The number that is a member of the subscription — its stable SCD recordId (not a per-version
     * `_id`), so the membership survives address edits. No ref: recordIds are not version `_id`s, so
     * this cannot be populated.
     */
    @validate({ zodValidator: ObjectIdString })
    @prop({ type: () => mongoose.Types.ObjectId, required: true })
    public addressRecordId!: mongoose.Types.ObjectId;

    static getters = {} as const satisfies Record<string, (self: NotificationSubscriptionMembership) => any>;

    static utilities = {} as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<NotificationSubscriptionMembershipSchemaTypes>;

    constructor(
        properties?: SchemaConstructorArgs<
            NotificationSubscriptionMembership,
            NotificationSubscriptionMembershipFieldsWithDefaults
        >,
    ) {
        super(properties);
        Object.assign(this, properties);
    }
}

NotificationSubscriptionMembership.dtoValidators = getZodSchemaFromClass(
    NotificationSubscriptionMembership,
) as unknown as DtoValidatorTypes<NotificationSubscriptionMembershipSchemaTypes>;
